import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { deleteUnnecessaryProperties, IListResponseData, PLATFORM, Platform, SERVICES_API } from '@pro-connect/shared';
import { Observable } from 'rxjs';
import { ITrainingsList } from '../../models/trainings-list';
import { ITrainingsListDto } from '../../models/trainings-list-dto';
import { IPlace } from '../../models/place';

@Injectable({
  providedIn: 'root',
})
export class TrainingsListService {
  constructor(
    @Inject(SERVICES_API)
    private readonly _servicesApi: string,
    @Inject(PLATFORM)
    private readonly _platform: Platform,
    private readonly _httpClient: HttpClient,
  ) {}

  /**
   * Get a list of trainings
   * @param dto
   */
  public getTrainings(dto: ITrainingsListDto): Observable<IListResponseData<ITrainingsList>> {
    const dtoWithRemovedBlankFields = deleteUnnecessaryProperties<ITrainingsListDto>(dto);
    return this._httpClient.get<IListResponseData<ITrainingsList>>(
      `${this._servicesApi}/pro/edu/training/${this._platform}/list`,
      {
        params: JSON.parse(JSON.stringify(dtoWithRemovedBlankFields)),
        withCredentials: true,
      },
    );
  }

  /**
   * Get a list of training/event categories
   */
  public getCategories(): Observable<{ id: number; name: string }[]> {
    return this._httpClient.get<{ id: number; name: string }[]>(`${this._servicesApi}/pro/edu/category/list`, {
      withCredentials: true,
    });
  }

  /**
   * Get a list of training/event subcategories per categories ids array
   */
  public getSubCategories(category_ids: number[]): Observable<{ id: number; name: string }[]> {
    const params = TrainingsListService._buildParamsDataSubCategories(category_ids);

    return this._httpClient.get<{ id: number; name: string }[]>(`${this._servicesApi}/pro/edu/sub-category/dropdown`, {
      withCredentials: true,
      params: params,
    });
  }

  /**
   * Get a list of training/event subcategories per categories ids array
   */
  public getSubCategoriesByCategoryId(id: number): Observable<{ id: number; name: string }[]> {
    return this._httpClient.get<{ id: number; name: string }[]>(
      `${this._servicesApi}/pro/edu/sub-category/${id}/categories`,
      {
        withCredentials: true,
      },
    );
  }

  /**
   * Get a list of trainings places
   */
  public getTrainingsPlaces(): Observable<IPlace[]> {
    return this._httpClient.get<IPlace[]>(`${this._servicesApi}/pro/edu/place/list?academy=1`, {
      withCredentials: true,
    });
  }

  /**
   * Get a list of trainings places
   */
  public getTrainingsEducators(): Observable<any[]> {
    return this._httpClient.get<any[]>(`${this._servicesApi}/pro/edu/educator/list`, {
      withCredentials: true,
    });
  }

  /**
   * Get a list of trainings places cities
   */
  public getTrainingsCities(): Observable<string[]> {
    return this._httpClient.get<string[]>(`${this._servicesApi}/pro/edu/place/cities`, {
      withCredentials: true,
    });
  }

  private static _buildParamsDataSubCategories(ids: number[]) {
    let params = new HttpParams();

    ids.forEach((id: number) => {
      params = params.append('category_ids[]', id);
    });
    return params;
  }
}
